import type { ReactNode } from "react";
import {
  BAR_OFFSET,
  CONTENT_BOTTOM,
  CONTENT_BOTTOM_WITH_BAR,
  INSET_TOP,
} from "../lib/insets";

/**
 * La shell di una schermata con barra inferiore fissa.
 *
 * I mockup disegnano un telaio 390×844, che è il modo di rappresentare un
 * telefono su una tavola: qui la schermata occupa il viewport vero e rispetta
 * le safe area. Su desktop resta centrata, in attesa del layout a due colonne.
 *
 * Il padding inferiore del contenuto tiene conto della barra: senza, l'ultima
 * riga della lista finirebbe sotto e resterebbe irraggiungibile. Le due misure
 * derivano dallo stesso `BAR_OFFSET`, perché scritte a mano avevano già preso
 * strade diverse.
 */
export interface MobileShellProps {
  children: ReactNode;
  bottom?: ReactNode;
  /**
   * Il fondo della schermata.
   *
   * `sunken` è la superficie incassata del design system — il grigio più chiaro
   * che abbiamo, `#F8F8F7` — ed è il fondo di serie: il contenuto dell'app è
   * fatto di schede bianche, che su bianco resterebbero visibili solo per il
   * filetto. `canvas` resta per le schermate che non hanno schede.
   */
  /** Il fondo della pagina. `canvas` (bianco) ovunque: vedi `app.css`. */
  tone?: "canvas" | "sunken";
}

export function MobileShell({ children, bottom, tone = "canvas" }: MobileShellProps) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: tone === "sunken" ? "var(--surface-sunken)" : "var(--surface-canvas)",
        position: "relative",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 430,
          margin: "0 auto",
          padding: `${INSET_TOP} var(--screen-gutter)`,
          paddingBottom: bottom ? CONTENT_BOTTOM_WITH_BAR : CONTENT_BOTTOM,
        }}
      >
        {children}
      </div>

      {bottom && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            padding: `0 var(--screen-gutter) ${BAR_OFFSET}`,
            pointerEvents: "none",
            viewTransitionName: "qota-barra",

            /*
              La barra si fotografa **da sola**, fuori dall'istantanea della
              pagina.

              Senza un nome proprio, una transizione fra schermate cattura tutto
              il documento come un unico blocco e lo fa scivolare — barra
              compresa. Ma la barra non è contenuto della schermata: è la
              cornice dell'app, e una cornice che scorre via insieme a ciò che
              incornicia è il contrario di quello che deve comunicare.
              `position: fixed` non basta: non ha voce in capitolo su cosa
              finisce nell'istantanea.

              Con un nome, il browser la tratta come un elemento a sé: sta nello
              stesso posto prima e dopo, quindi non si muove. La dissolvenza
              incrociata che resta è utile — serve a far cambiare in morbido la
              voce attiva quando si passa da una sezione all'altra.
            */
            
          }}
        >
          {/*
            La barra è larga **quanto il suo contenuto**, non quanto lo schermo.

            Prima si stirava fino a 430px e le icone si allontanavano fra loro
            per riempire il vuoto. Ma una barra di comandi non è un contenitore
            da riempire: è un gruppo di bersagli, e la sua misura giusta è la
            somma dei bersagli — quattro destinazioni da 46, il filetto, il più.
            Circa 268px, che su un telefono da 390 è un quarto più stretta.

            `fit-content` e non una percentuale: aggiungendo una destinazione la
            barra cresce da sé invece di comprimere le altre, e una percentuale
            scelta oggi sarebbe sbagliata domani.
          */}
          <div
            style={{
              width: "fit-content",
              // Su uno schermo molto stretto il contenuto vince e la barra
              // sborderebbe: qui si ferma al bordo dello schermo.
              maxWidth: "100%",
              margin: "0 auto",
              pointerEvents: "auto",
            }}
          >
            {bottom}
          </div>
        </div>
      )}
    </div>
  );
}
